import { useState, useEffect, useRef } from "react";

const WHY_US = [
  {
    icon: "ri-ai-generate",
    title: "AI matches in under 4 minutes",
    detail: "Scans hundreds of local openings and ranks by pay, distance, and your exact skills — instantly.",
    accent: "border-emerald-400",
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    tag: "Fastest on the market",
    tagColor: "bg-emerald-50 text-emerald-700",
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "No degree. No resume. Just skills.",
    detail: "We match what you can actually do — not where you went to school.",
    accent: "border-amber-400",
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
    tag: "Skills-first platform",
    tagColor: "bg-amber-50 text-amber-700",
  },
  {
    icon: "ri-timer-flash-line",
    title: "Same-day gigs every day",
    detail: "Hundreds of employers post urgent work every morning. Show up, get paid.",
    accent: "border-orange-400",
    iconBg: "bg-orange-50",
    iconColor: "text-orange-500",
    tag: "Same-day payout",
    tagColor: "bg-orange-50 text-orange-700",
  },
  {
    icon: "ri-shield-check-line",
    title: "Verified employers only",
    detail: "Every employer is identity-checked before posting. No scams, no ghosting.",
    accent: "border-rose-400",
    iconBg: "bg-rose-50",
    iconColor: "text-rose-500",
    tag: "100% verified",
    tagColor: "bg-rose-50 text-rose-700",
  },
  {
    icon: "ri-bar-chart-box-line",
    title: "Earn more as you grow",
    detail: "Add capabilities to unlock premium jobs and climb to higher pay tiers automatically.",
    accent: "border-sky-400",
    iconBg: "bg-sky-50",
    iconColor: "text-sky-600",
    tag: "Growth built-in",
    tagColor: "bg-sky-50 text-sky-700",
  },
  {
    icon: "ri-map-pin-2-line",
    title: "Live in 120+ US cities",
    detail: "From major metros to mid-size cities — active in your area and expanding weekly.",
    accent: "border-violet-400",
    iconBg: "bg-violet-50",
    iconColor: "text-violet-600",
    tag: "Nationwide",
    tagColor: "bg-violet-50 text-violet-700",
  },
];

const STATS = [
  { value: "$4.2M+", label: "Paid out this month", icon: "ri-money-dollar-circle-line" },
  { value: "12,400+", label: "Active workers", icon: "ri-group-line" },
  { value: "4 min", label: "Avg. first match", icon: "ri-timer-flash-line" },
  { value: "120+", label: "Cities live", icon: "ri-map-pin-2-line" },
];

export default function CommunitySection() {
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText("GIGNOW50").then(() => {
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) { setEmailSent(true); setEmail(""); }
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <section className="bg-white overflow-hidden">

      {/* ── Full-width dark stats banner ── */}
      <div className="bg-gray-950 px-4 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-gray-400 text-sm font-semibold uppercase tracking-widest">By the numbers</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-800 rounded-2xl overflow-hidden">
            {STATS.map((s) => (
              <div key={s.label} className="bg-gray-950 flex flex-col items-center justify-center py-8 px-4 text-center gap-2">
                <i className={`${s.icon} text-emerald-400 text-xl`} />
                <div className="text-3xl md:text-4xl font-black text-white leading-none">{s.value}</div>
                <div className="text-xs text-gray-500 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Why Us + Offers ── */}
      <div className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-4 py-1.5 rounded-full mb-4">
              Why It Works
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
              Built for real workers.
              <span className="text-emerald-500"> Not just resumes.</span>
            </h2>
            <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto">
              The only platform that matches you on what you can <em>actually do</em> — not your work history.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Why Us grid */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {WHY_US.map((item) => (
                <div
                  key={item.title}
                  className={`bg-white border-l-4 ${item.accent} rounded-r-2xl rounded-l-sm p-5 flex flex-col gap-3 border border-l-4 border-gray-100 hover:border-gray-200 transition-all duration-200 group`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className={`w-10 h-10 flex items-center justify-center ${item.iconBg} rounded-xl flex-shrink-0`}>
                      <i className={`${item.icon} ${item.iconColor} text-lg`} />
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${item.tagColor}`}>
                      {item.tag}
                    </span>
                  </div>
                  <div>
                    <div className="font-extrabold text-gray-900 text-sm mb-1.5">{item.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{item.detail}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Offer cards */}
            <div className="flex flex-col gap-4">

              {/* 50% OFF promo */}
              <div className="relative rounded-2xl overflow-hidden bg-gray-950 p-5">
                <div className="pointer-events-none absolute -top-8 -right-8 w-36 h-36 rounded-full bg-emerald-500/10" />
                <div className="pointer-events-none absolute bottom-0 left-4 w-20 h-20 rounded-full bg-emerald-400/5" />
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 flex items-center justify-center bg-emerald-500/20 rounded-lg flex-shrink-0">
                    <i className="ri-percent-line text-emerald-400 text-sm" />
                  </div>
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-400">Limited Offer</span>
                </div>
                <div className="text-white text-4xl font-black mb-0.5">50% OFF</div>
                <div className="text-gray-300 font-bold text-sm mb-1">First Month Premium</div>
                <div className="text-gray-500 text-xs mb-5 leading-relaxed">
                  AI matching, priority listings &amp; instant payouts. Cancel anytime.
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Promo Code</div>
                    <div className="text-white font-extrabold text-base tracking-[0.2em]">GIGNOW50</div>
                  </div>
                  <button
                    onClick={handleCopy}
                    className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-all ${copied ? "bg-emerald-500 text-white" : "bg-emerald-500 hover:bg-emerald-400 text-white"}`}
                  >
                    <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
                <div className="flex items-center gap-1.5 text-gray-600 text-xs">
                  <i className="ri-time-line text-gray-500" />
                  Expires in 3 days · 142 spots left
                </div>
              </div>

              {/* Refer & Earn */}
              <div className="bg-[#fffbf0] border border-amber-100 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                      <i className="ri-gift-line text-amber-600 text-sm" />
                    </div>
                    <span className="text-xs font-extrabold uppercase tracking-widest text-amber-700">Refer &amp; Earn</span>
                  </div>
                  <span className="text-xl font-black text-amber-600">$25</span>
                </div>
                <div className="text-gray-900 font-extrabold text-base mb-1">Per successful referral</div>
                <p className="text-gray-500 text-xs leading-relaxed mb-4">
                  Invite anyone. When they complete their first gig, you both get $25. No cap.
                </p>
                <div className="flex items-center gap-1.5 mb-4">
                  {[
                    { icon: "ri-user-add-line", label: "They join" },
                    { icon: "ri-briefcase-line", label: "First gig" },
                    { icon: "ri-money-dollar-circle-line", label: "+$25 each" },
                  ].map((step, i) => (
                    <div key={i} className="flex items-center gap-1.5 flex-1">
                      <div className="flex flex-col items-center gap-1 flex-shrink-0">
                        <div className="w-8 h-8 flex items-center justify-center bg-white border border-amber-200 rounded-full">
                          <i className={`${step.icon} text-amber-600 text-sm`} />
                        </div>
                        <span className={`text-[9px] font-semibold whitespace-nowrap ${i === 2 ? "text-emerald-600" : "text-gray-400"}`}>{step.label}</span>
                      </div>
                      {i < 2 && <div className="flex-1 h-px bg-amber-200 mb-4" />}
                    </div>
                  ))}
                </div>
                <a href="/signup" className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl text-xs transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-share-line" />
                  Start Referring Now
                </a>
              </div>

              {/* Weekly digest */}
              <div className="bg-[#f7f9f8] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 flex items-center justify-center bg-rose-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-send-line text-rose-500 text-sm" />
                  </div>
                  <div>
                    <div className="text-xs font-extrabold text-gray-900">Weekly Jobs Digest</div>
                    <div className="text-[10px] text-gray-400">Free · Every Monday</div>
                  </div>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed mb-3">
                  Top 10 highest-paying gigs near you, straight to your inbox.
                </p>
                {emailSent ? (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
                    <i className="ri-check-double-line text-emerald-600" />
                    <span className="text-xs font-bold text-emerald-700">You&apos;re in! See you Monday.</span>
                  </div>
                ) : (
                  <form onSubmit={handleEmailSubmit} className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="flex-1 min-w-0 border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-rose-300 bg-white"
                    />
                    <button type="submit" className="px-3 py-2 bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-xl text-xs transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                      <i className="ri-send-plane-fill" />
                    </button>
                  </form>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

    </section>
  );
}
